import _ from 'underscore';
import { actionItemStrings } from './strings';

const SuggestionState = {
  Met: 'met',
  HalfMet: 'half-met',
  NotMet: 'not-met',
};

const StrengthScore = {
  Poor: 25,
  Average: 50,
  Good: 75,
  Excellent: 100,
};

const NewStrengthScore = {
  Incomplete: 7,
  Poor: 14,
  Average: 49,
  Good: 83,
  Excellent: 98,
};

const StrengthColor = {
  Poor: '#BC0505',
  Average: '#F2610C',
  Good: '#107C10',
  Excellent: '#107C10',
};

const StrengthIcon = {
  Poor: 'Error',
  Average: 'Warning',
  Good: 'Completed',
  Excellent: 'Completed',
};

export const maxHeadlineCount = 15;

export const maxDescriptionCount = 4;

export const ViewSuggestionType = {
  None: 'none',
  Headline: 'headline',
  Description: 'description',
};

export const assetStrMap = {
  Headlines: 'headline',
  Descriptions: 'description',
};

export const assetTypeMap = {
  headline: 'Headlines',
  description: 'Descriptions',
};

export function getStrengthScore(strength) {
  return StrengthScore[strength] || 0;
}

export function getNewStrengthScore(strength) {
  return NewStrengthScore[strength] || 0;
}

export function getStrengthColor(strength) {
  return StrengthColor[strength] || '#BC0505';
}

export function getStrengthIcon(strength) {
  return StrengthIcon[strength] || 'Error';
}

export function newGenerateSuggestion({
  i18n,
  actionItem,
  xValue,
  priority,
}) {
  let suggestion;
  switch (actionItem) {
    case 'AddMoreHeadlines': {
      suggestion = i18n.getString(_TL_('Add {{x}} more headlines.'), { x: xValue });
      break;
    }
    case 'AddMoreDescriptions': {
      suggestion = i18n.getString(_TL_('Add {{x}} more descriptions.'), { x: xValue });
      break;
    }
    case 'AddLongerHeadlines': {
      suggestion = i18n.getString(_TL_('Create longer headlines. Your current average number of characters is {{x}}.'), { x: xValue });
      break;
    }
    case 'AddLongerDescriptions': {
      suggestion = i18n.getString(_TL_('Create longer Descriptions. Your current average number of characters is {{x}}.'), { x: xValue });
      break;
    }
    case 'UnpinHeadlines': {
      suggestion = i18n.getString(_TL_('Unpin headline for more combination.'));
      break;
    }
    case 'UnpinDescriptions': {
      suggestion = i18n.getString(_TL_('Unpin description for more combination.'));
      break;
    }
    case 'IncludeKeywordsOrKeywordInsertionHeadlines': {
      const keywordList = xValue.split('#');
      if (keywordList.length === 0) {
        suggestion = i18n.getString(_TL_('Include popular keywords in headlines.'));
      } else if (keywordList.length===1) {
        suggestion = i18n.getString(_TL_('Include a popular keyword in headlines such as "{{x}}".'), { x: keywordList[0] });
      } else {
        suggestion = i18n.getString(_TL_('Include a popular keyword in headlines such as "{{x}}" or "{{y}}".'), { x: keywordList[0], y: keywordList[1] });
      }
      break;
    }
    case 'IncludeKeywordsOrKeywordInsertionDescriptions': {
      const keywordList = xValue.split('#');
      if (keywordList.length === 0) {
        suggestion = i18n.getString(_TL_('Include popular keywords in descriptions.'));
      } else if (keywordList.length===1) {
        suggestion = i18n.getString(_TL_('Include a popular keyword in descriptions such as "{{x}}".'), { x: keywordList[0] });
      } else {
        suggestion = i18n.getString(_TL_('Include a popular keyword in descriptions such as "{{x}}" or "{{y}}".'), { x: keywordList[0], y: keywordList[1] });
      }
      break;
    }
    case 'MakeHeadlinesUnique': {
      suggestion = i18n.getString(_TL_('Try to diversify your headlines.'));
      break;
    }
    case 'MakeDescriptionsUnique': {
      suggestion = i18n.getString(_TL_('Try to diversify your descriptions.'));
      break;
    }
    default:
      break;
  }
  return {
    suggestion,
    priority,
  };
}


export function generateSuggestion({
  i18n,
  assets,
  actionItem,
  strength,
  priority,
  length,
}) {
  const suggestion = i18n.getString(actionItemStrings[actionItem]);
  let state = 'not-met';
  let suggestionType = ViewSuggestionType.None;

  switch (actionItem) {
    case 'AddMoreHeadlines': {
      const headlineCount = assets.headlines.length;
      if (headlineCount < 5) {
        state = SuggestionState.NotMet;
      } else if (headlineCount >= maxHeadlineCount
        || (headlineCount > 8 && getStrengthScore(strength) >= StrengthScore.Good)) {
        state = SuggestionState.Met;
      } else {
        state = SuggestionState.HalfMet;
      }
      suggestionType = ViewSuggestionType.Headline;
      break;
    }
    case 'AddMoreDescriptions': {
      const descriptionCount = assets.descriptions.length;
      if (descriptionCount < 2) {
        state = SuggestionState.NotMet;
      } else if (descriptionCount >= maxDescriptionCount
        || (descriptionCount > 3 && getStrengthScore(strength) >= StrengthScore.Good)) {
        state = SuggestionState.Met;
      } else {
        state = SuggestionState.HalfMet;
      }
      suggestionType = ViewSuggestionType.Description;
      break;
    }
    case 'DistinctHeadlines': {
      state = _.uniq(assets.headlines).length === assets.headlines.length
        ? SuggestionState.HalfMet
        : SuggestionState.NotMet;
      suggestionType = ViewSuggestionType.Headline;
      break;
    }
    case 'DistinctDescriptions':
      state = _.uniq(assets.descriptions).length === assets.descriptions.length
        ? SuggestionState.HalfMet
        : SuggestionState.NotMet;
      suggestionType = ViewSuggestionType.Description;
      break;
    case 'LongerHeadlines':
      state = (_.size(assets.headlines) === 0
        || _.any(assets.headlines, headline => _.size(headline) < 10))
        ? SuggestionState.NotMet
        : SuggestionState.HalfMet;
      suggestionType = ViewSuggestionType.Headline;
      break;
    case 'LongerDescriptions':
      state = (_.size(assets.descriptions) === 0
        || _.any(assets.descriptions, description => _.size(description) < 10))
        ? SuggestionState.NotMet
        : SuggestionState.HalfMet;
      suggestionType = ViewSuggestionType.Description;
      break;
    case 'UnpinHeadlinePositions': {
      const pinCount = assets.headlinePins.length;
      if (pinCount > 0) {
        state = pinCount === assets.headlines.length
          ? SuggestionState.NotMet
          : SuggestionState.HalfMet;
      } else {
        state = SuggestionState.Met;
      }
      suggestionType = ViewSuggestionType.None;
      break;
    }
    case 'UnpinDescriptionPositions': {
      const pinCount = assets.descriptionPins.length;
      if (pinCount > 0) {
        state = pinCount === assets.descriptions.length
          ? SuggestionState.NotMet
          : SuggestionState.HalfMet;
      } else {
        state = SuggestionState.Met;
      }
      suggestionType = ViewSuggestionType.None;
      break;
    }
    case 'MoreCombinations':
      if (assets.headlines.length < 5 || assets.descriptions.length < 3) {
        state = SuggestionState.NotMet;
      } else {
        state = SuggestionState.HalfMet;
      }
      if (assets.headlines.length < 10) {
        suggestionType = ViewSuggestionType.Headline;
      } else if (assets.descriptions.length < maxDescriptionCount) {
        suggestionType = ViewSuggestionType.Description;
      } else {
        suggestionType = ViewSuggestionType.None;
      }
      break;
    default:
      break;
  }

  return {
    action: actionItem,
    state,
    suggestion,
    suggestionType,
    priority: actionItem === 'MoreCombinations' ? length : priority,
  };
}

export function sortSuggestions(suggestions) {
  const orderFromMT = {};
  const currentOrders = {
    AddMoreHeadlines: 1,
    AddMoreDescriptions: 2,
    LongerHeadlines: 3,
    LongerDescriptions: 4,
    DistinctHeadlines: 5,
    DistinctDescriptions: 6,
    UnpinHeadlinePositions: 7,
    UnpinDescriptionPositions: 8,
    MoreCombinations: 9,
  };
  suggestions.forEach((suggestion) => {
    orderFromMT[`${suggestion.action}-not-met`] = suggestion.priority + ((currentOrders[suggestion.action] || 0) / 10);
    orderFromMT[`${suggestion.action}-half-met`] = suggestion.priority + suggestions.length + ((currentOrders[suggestion.action] || 0) / 10);
    orderFromMT[`${suggestion.action}-met`] = suggestion.priority + (suggestions.length * 2) + ((currentOrders[suggestion.action] || 0) / 10);
  });
  return _.sortBy(suggestions, suggestion => orderFromMT[`${suggestion.action}-${suggestion.state}`] || 100);
}

export function isViewSuggestionApplied(assets, suggestionContent = '') {
  if (_.isEmpty(assets)) return false;
  const index = _.findIndex(_.range(assets.length), i =>
    _.isString(assets[i].Asset.Text) &&
    suggestionContent.toLowerCase() === assets[i].Asset.Text.toLowerCase());

  return (index !== -1);
}
